import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import LeadSearchInput from '../leads/LeadSearchInput';
import { DeleteConfirmationModal, useNotification, TabNavigation } from '../common';
import { addUndoNotification } from '../common/NotificationManager';
import TasksTableWithShared from './TasksTableWithShared';
import EditTaskModal from './EditTaskModal'; // Import the new modal
import LeadDetailsModal from '../leads/LeadDetailsModal'; // Import LeadDetailsModal
import Button from '../common/Button';

const TasksList = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('tasksActiveTab') || 'pending'; // 'pending' or 'completed'
  });
  
  // Lead details modal state
  const [showLeadDetailsModal, setShowLeadDetailsModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // Task selection states for bulk actions
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkActionData, setBulkActionData] = useState({
    status: '',
    due_date: '',
    due_time: '',
    priority: '',
    assigned_to: '',
    type: ''
  });

  // Import useNotification hook
  const { showNotification } = useNotification();
  const notificationTimeoutRef = useRef(null);

  // Filters state with localStorage persistence
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('tasksFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      status: '',
      due_date_after: '',
      due_date_before: '',
      assigned_to: '',
      type: ''
    };
  });
  
  // Initial active filters from localStorage with ref initialization
  const savedActiveFilters = localStorage.getItem('tasksActiveFilters');
  const initialActiveFilters = savedActiveFilters ? JSON.parse(savedActiveFilters) : {};
  const [activeFilters, setActiveFilters] = useState(initialActiveFilters);
  
  // Ref to help with filter state updates, initialized with localStorage value
  const activeFiltersRef = useRef(initialActiveFilters);

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().substr(0, 10); // Format as YYYY-MM-DD
  };

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: getTodayDate(), // Set default to today (for form display)
    due_time: '23:59',  // Default to end of day (23:59)
    priority: 'medium',
    assigned_to: '', // This will be set to current user ID in useEffect
    lead_id: '', // Add lead_id field
    status: 'pending',
    type: 'task' // Default type is task (vs support)
  });
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);

  // Sorting state with localStorage persistence
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('tasksSortBy') || 'due_at');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('tasksSortOrder') || 'asc');

  // Column management state
  const [showColumnModal, setShowColumnModal] = useState(false);

  // Pagination state
  // Initialize limit from localStorage or default to 20
  const initialLimit = parseInt(localStorage.getItem('tasksTableLimit') || '20', 10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: [20, 50, 100].includes(initialLimit) ? initialLimit : 20, // Ensure valid limit
    total: 0,
    pages: 0
  });

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // For all cases, just directly update the filter value
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters with localStorage persistence
  const applyFilters = () => {
    // Process the date filter selection into proper API parameters
    const processedFilters = {};
    
    // First, add any non-date filters
    if (filters.assigned_to) processedFilters.assigned_to = filters.assigned_to;
    if (filters.type) processedFilters.type = filters.type;

    // Handle date filtering - completely rewritten
    const dateSelection = filters.due_date_after;
    
    if (dateSelection) {
      // Get fresh date objects for this operation
      const now = new Date();
      
      // Set to beginning of day without timezone manipulations
      // We're handling date ranges here, not absolute timestamps
      const todayDateString = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const todayStart = `${todayDateString}T00:00:00`;
      const todayEnd = `${todayDateString}T23:59:59`;
      
      console.log('Date strings for filters:', {
        todayDateString,
        todayStart,
        todayEnd
      });
      
      // Handle each case
      if (dateSelection === 'today') {
        // IMPORTANT: For today filter, use date-based filtering for the current local day
        processedFilters.due_before = todayEnd; 
        processedFilters.due_after = todayStart;
        console.log('TODAY FILTER:', {
          due_before: processedFilters.due_before,
          due_after: processedFilters.due_after
        });
      } 
      else if (dateSelection === 'overdue') {
        // For overdue tasks, we need two conditions:
        // 1. Either the task is from a previous date (due_at < today's start)
        // 2. OR the task is from today but the current time is past its due time
        
        // For #1, find tasks with dates before today
        processedFilters.due_before_or_now = todayStart;
        
        // For #2, add special flag for tasks due today but now past due time
        // The backend will handle this with a special case
        processedFilters.include_past_due_today = true;
        
        console.log('OVERDUE FILTER:', {
          due_before_or_now: processedFilters.due_before_or_now,
          include_past_due_today: true,
          current_timestamp: new Date().toISOString()
        });
      } 
      else if (dateSelection === 'next7days') {
        const nextWeekDate = new Date(now);
        nextWeekDate.setDate(nextWeekDate.getDate() + 7);
        const nextWeekDateString = nextWeekDate.toISOString().split('T')[0];
        const nextWeekEnd = `${nextWeekDateString}T23:59:59`;
        
        processedFilters.due_after = todayStart;
        processedFilters.due_before = nextWeekEnd;
      } 
      else if (dateSelection === 'next30days') {
        const nextMonthDate = new Date(now);
        nextMonthDate.setDate(nextMonthDate.getDate() + 30);
        const nextMonthDateString = nextMonthDate.toISOString().split('T')[0];
        const nextMonthEnd = `${nextMonthDateString}T23:59:59`;
        
        processedFilters.due_after = todayStart;
        processedFilters.due_before = nextMonthEnd;
      }
    }
    
    // Create properly formatted active filters to match EXACTLY what the backend API expects
    const newActiveFilters = {};
    
    if (processedFilters.assigned_to) newActiveFilters.assigned_to = processedFilters.assigned_to;
    if (processedFilters.type) newActiveFilters.type = processedFilters.type;
    // The backend API expects these specific parameter names!
    if (processedFilters.due_before) newActiveFilters.due_before = processedFilters.due_before;
    if (processedFilters.due_after) newActiveFilters.due_after = processedFilters.due_after;
    // Add new fields for overdue filtering
    if (processedFilters.due_before_or_now) newActiveFilters.due_before_or_now = processedFilters.due_before_or_now;
    if (processedFilters.include_past_due_today) newActiveFilters.include_past_due_today = processedFilters.include_past_due_today;
    
    // Add specific log to debug exactly what we're sending
    console.log('ACTUAL FILTERS BEING SENT TO API:', {
      due_before: newActiveFilters.due_before,
      due_after: newActiveFilters.due_after,
      assigned_to: newActiveFilters.assigned_to,
      type: newActiveFilters.type
    });
    
    // Save to localStorage
    localStorage.setItem('tasksActiveFilters', JSON.stringify(newActiveFilters));
    localStorage.setItem('tasksFilters', JSON.stringify(filters));
    
    // Update state with the new filters
    setActiveFilters(newActiveFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset page on filter apply
      
    // Use existing ref to store filter data to prevent timing issues with state updates
    activeFiltersRef.current = newActiveFilters;
    
    // Explicitly fetch tasks after state updates
    fetchTasks(1);

    // Close filters panel on mobile
    if (window.innerWidth < 768) {
      setShowFilters(false);
    }
  };

  // Clear filters and remove from localStorage
  const clearFilters = () => {
    const emptyFilters = {
      status: '',
      due_date_after: '',
      due_date_before: '',
      assigned_to: '',
      type: ''
    };
    
    // Clear localStorage
    localStorage.removeItem('tasksActiveFilters');
    localStorage.setItem('tasksFilters', JSON.stringify(emptyFilters));
    
    // Update form filters
    setFilters(emptyFilters);
    
    // Set empty activeFilters object with correct field names for backend
    const emptyActiveFilters = {};
    setActiveFilters(emptyActiveFilters);
    
    // Update ref for immediate use
    activeFiltersRef.current = emptyActiveFilters;
    
    // Reset page on filter clear
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Explicitly fetch with cleared filters
    fetchTasks(1);
  };

  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  // Generate date range options for filtering
  const getDateRangeOptions = () => {
    const today = new Date();
    const startOfToday = new Date(today.setHours(0, 0, 0, 0)).toISOString();

    // Next 7 days
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    // Next 30 days
    const nextMonth = new Date(today);
    nextMonth.setDate(today.getDate() + 30);

    // Overdue (before today)
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const endOfYesterday = new Date(yesterday);
    endOfYesterday.setHours(23, 59, 59, 999);

    return [
      { label: 'Today', value: 'today' },
      { label: 'Next 7 Days', value: 'next7days' },
      { label: 'Next 30 Days', value: 'next30days' },
      { label: 'Overdue', value: 'overdue' }
    ];
  };

  // Define fetchTasks outside useEffect, wrapped in useCallback
  // Modified to accept an optional limit override
  const fetchTasks = useCallback(async (page = pagination.page, limitOverride = null) => {
    const currentLimit = limitOverride !== null ? limitOverride : pagination.limit;
    // Removed console.log
    try {
      setLoading(true);
        const token = localStorage.getItem('token');

        // Always prefer the ref value for the most up-to-date filters
        // Fallback to state only if ref is empty
        const refFilters = activeFiltersRef.current || {};
        const stateFilters = activeFilters || {};
        
        // Choose ref if it has filters, otherwise use state
        const filterSource = Object.keys(refFilters).length > 0 ? refFilters : stateFilters;
        console.log('Using filters from:', Object.keys(refFilters).length > 0 ? 'ref' : 'state');
        
        // Clone to avoid mutation
        const baseFilters = { ...filterSource };
        delete baseFilters.status; // Ensure status from filter panel doesn't interfere

        // Determine status filter based on activeTab
        // Assuming backend accepts comma-separated list for multiple statuses
        let statusFilter = '';
        if (activeTab === 'pending') {
          statusFilter = 'pending'; 
        } else if (activeTab === 'completed') {
          statusFilter = 'completed';
        }
        // If no specific tab status, don't send the status param (or send empty string if required)

        const config = {
          headers: {
            'x-auth-token': token
          },
          params: {
            ...baseFilters, // Apply other active filters
            ...(statusFilter && { status: statusFilter }), // Add status filter only if needed
            page: page,
            limit: currentLimit, 
            sort_by: sortBy,
            sort_order: sortOrder
          }
        };
        
        console.log('Fetching tasks with filters:', config.params);

        // Removed console.log

        // Using the real API endpoint with filters including status based on tab
        const res = await axios.get(`/api/tasks`, config);
        // Removed console.log
 
         // Extract pagination info if available
         if (res.data.pagination) {
           // Update pagination state based on API response
           setPagination({ 
             page: parseInt(res.data.pagination.page, 10),
            limit: parseInt(res.data.pagination.limit, 10), 
            total: parseInt(res.data.pagination.total, 10),
             pages: parseInt(res.data.pagination.pages, 10)
           });
           // Removed console.log
 
           // Directly set tasks from API response - Backend now handles filtering by status
           setTasks(res.data.tasks || []);

         } else {
           // Removed console.log
           // Fallback for backward compatibility (if pagination object is missing)
           // This part might need adjustment if the non-paginated response structure differs
           setTasks(res.data || []); 
           // Set default pagination if none provided by API
           setPagination({
            page: 1,
            limit: currentLimit, 
             total: (res.data || []).length,
             pages: Math.ceil((res.data || []).length / currentLimit)
           });
           // Removed console.log
         }
 
         setError('');
      } catch (err) {
        console.error('Error fetching tasks:', err);
        // Removed console.log
        setError('Failed to load tasks. Please try again later.');
      } finally {
        setLoading(false);
    }
  // Add activeFiltersRef to dependencies in addition to activeFilters
  }, [activeFilters, activeFiltersRef, sortBy, sortOrder, activeTab, pagination.page, pagination.limit]);

  // Define fetchUsers outside useEffect, wrapped in useCallback
  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

        const config = {
          headers: {
            'x-auth-token': token
          }
        };

        const res = await axios.get('/api/auth/users', config);
        setUsers(res.data);
      } catch (err) {
        console.error('Error fetching users:', err);
    }
  // fetchUsers doesn't seem to have dependencies within this component's state/props
  }, []);

  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    // Only update state, let useEffect handle the fetch
    setPagination(prev => ({ ...prev, page: newPage })); 
  };

  // Handle limit change for pagination
  const handleLimitChange = (newLimit) => {
    const limitValue = parseInt(newLimit, 10);
    if (![20, 50, 100].includes(limitValue)) return; // Ignore invalid values

    console.log(`Changing limit to ${limitValue}`);
    // Save the new limit to localStorage
    localStorage.setItem('tasksTableLimit', limitValue.toString());
    // Update pagination state first, resetting page to 1
    setPagination(prev => ({ ...prev, limit: limitValue, page: 1 }));
    // Let useEffect handle the fetch
  };

  // Handle sorting with localStorage persistence
  const handleSort = (columnId) => {
    let newSortOrder;
    if (sortBy === columnId) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newSortOrder = 'asc'; // Default to ascending for a new column
    }
    
    // Save to localStorage
    localStorage.setItem('tasksSortBy', columnId);
    localStorage.setItem('tasksSortOrder', newSortOrder);
    
    setSortOrder(newSortOrder);
    setSortBy(columnId);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset page on sort change
  };

  // Add effect to get current user's ID and set as default assigned_to
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'x-auth-token': token
          }
        };

        const res = await axios.get('/api/auth/me', config);
        if (res.data && res.data.id) {
          // Set the current user as default assigned_to
          setNewTask(prev => ({
            ...prev,
            assigned_to: res.data.id
          }));
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };

    getCurrentUser();
  }, []);


  // New effect that runs only once on component mount to apply saved filters
  useEffect(() => {
    // On component mount, if we have active filters from localStorage,
    // explicitly ensure the filter reference data is set correctly
    if (Object.keys(initialActiveFilters).length > 0) {
      console.log('Found saved filters on component mount:', initialActiveFilters);
      activeFiltersRef.current = initialActiveFilters;
      
      // No need to call fetchTasks here, the regular effect below will do that
      // with the correct activeFilters from state
    }
  }, []); // Empty dependency array means it runs once on mount
  
  // useEffect for fetching data when relevant state changes
  useEffect(() => {
    // Fetch tasks whenever relevant state changes (filters, sort, tab, page, limit)
    // Pass the current page and limit from the state to fetchTasks
    fetchTasks(pagination.page, pagination.limit); 
    fetchUsers();
  // Add pagination.page and pagination.limit to dependencies
  }, [activeFilters, activeTab, sortBy, sortOrder, fetchTasks, fetchUsers, pagination.page, pagination.limit]); 
  
  // Set up global function to show lead details modal
  useEffect(() => {
    // Create a global function to show the lead details modal from the table
    window.showLeadDetailsModal = (leadId) => {
      // Fetch lead details
      const fetchLead = async () => {
        try {
          const token = localStorage.getItem('token');
          const config = {
            headers: {
              'x-auth-token': token
            }
          };
          
          const res = await axios.get(`/api/leads/${leadId}`, config);
          setSelectedLead(res.data);
          setShowLeadDetailsModal(true);
        } catch (err) {
          console.error('Error fetching lead:', err);
          showNotification('Error loading lead details', 'error');
        }
      };
      
      fetchLead();
    };
    
    // Clean up function
    return () => {
      delete window.showLeadDetailsModal;
    };
  }, [showNotification]);
  
  // Handle ESC key to close modals
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showNewTaskModal) {
          setShowNewTaskModal(false);
        }
        if (showEditTaskModal) {
          setShowEditTaskModal(false);
        }
        if (showBulkActionModal) {
          setShowBulkActionModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscKey);

    // Clean up event listener
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [showNewTaskModal, showEditTaskModal, showBulkActionModal]);

  // Execute bulk actions
  const executeBulkAction = async (action, actionData) => {
    try {
      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      let updatedTasks = [...tasks];

      if (action === 'update') {
        // Update selected tasks
        const updateData = {};

        // Only include fields with values
        if (actionData.status) updateData.status = actionData.status;
        if (actionData.due_date) updateData.due_date = actionData.due_date;
        if (actionData.due_time) updateData.due_time = actionData.due_time;
        if (actionData.priority) updateData.priority = actionData.priority;
        if (actionData.assigned_to) {
          // Handle special case for unassigning (value is "null" string)
          if (actionData.assigned_to === "null") {
            updateData.assigned_to = null;
          } else {
            // Convert string ID back to number to ensure it's sent as an integer
            updateData.assigned_to = parseInt(actionData.assigned_to, 10);
          }
        }
        if (actionData.type) updateData.type = actionData.type;

        // Log the task structure to see what's happening
        console.log('Tasks array sample:', tasks.length > 0 ? {
          taskExample: tasks[0],
          assigned_to_type: typeof tasks[0].assigned_to,
          assigned_to_value: tasks[0].assigned_to
        } : 'No tasks');
        
        // Update tasks one by one
        for (const taskId of selectedTasks) {
          const taskToUpdate = tasks.find(task => task.id === taskId);
          console.log('Task to update:', {
            id: taskId,
            foundTask: taskToUpdate ? true : false,
            assigned_to_type: taskToUpdate ? typeof taskToUpdate.assigned_to : 'N/A',
            assigned_to_value: taskToUpdate ? taskToUpdate.assigned_to : 'N/A'
          });
          if (taskToUpdate) {
            // Create a clean task object with only the properties needed by the API
            // This prevents sending unwanted properties like assigned_to as a string name
            const cleanTask = {
              title: taskToUpdate.title,
              description: taskToUpdate.description,
              // Convert dates if needed
              due_at: taskToUpdate.due_at,
              priority: taskToUpdate.priority,
              status: taskToUpdate.status,
              type: taskToUpdate.type || 'task',
              // Handle lead_id - ensure it's an ID not an object
              lead_id: taskToUpdate.lead_id && typeof taskToUpdate.lead_id === 'object' 
                ? taskToUpdate.lead_id.id 
                : taskToUpdate.lead_id
            };
            
            // Handle date and time separately for due_at
            if (updateData.due_date) {
              // If we have both date and time
              if (updateData.due_time) {
                // Combine date and time into an ISO string
                const dateStr = updateData.due_date;
                const timeStr = updateData.due_time;
                const [year, month, day] = dateStr.split('-');
                const [hours, minutes] = timeStr.split(':');
                
                // Create a new date with the combined values
                const dueDate = new Date(
                  parseInt(year), 
                  parseInt(month) - 1, // Month is 0-indexed in JS
                  parseInt(day),
                  parseInt(hours),
                  parseInt(minutes)
                );
                
                cleanTask.due_at = dueDate.toISOString();
                console.log('Set due_at with time:', {
                  date: updateData.due_date,
                  time: updateData.due_time,
                  result: cleanTask.due_at
                });
              } 
              // If we only have date but no time, use end of day
              else {
                // Parse the date string
                const [year, month, day] = updateData.due_date.split('-');
                
                // Create date at end of day (23:59)
                const dueDate = new Date(
                  parseInt(year), 
                  parseInt(month) - 1, 
                  parseInt(day), 
                  23, 59
                );
                
                cleanTask.due_at = dueDate.toISOString();
                console.log('Set due_at with default time:', cleanTask.due_at);
              }
            }
            
            // Apply other updates from updateData
            Object.keys(updateData).forEach(key => {
              // Skip assigned_to (handled separately) and due_date/due_time (already handled)
              if (key !== 'assigned_to' && key !== 'due_date' && key !== 'due_time') {
                cleanTask[key] = updateData[key];
              }
            });
            
            // Special handling for assigned_to
            if ('assigned_to' in updateData) {
              if (updateData.assigned_to === null) {
                cleanTask.assigned_to = null;
              } else {
                // Try to convert to a valid ID
                const assignedId = parseInt(updateData.assigned_to, 10);
                cleanTask.assigned_to = isNaN(assignedId) ? null : assignedId;
              }
            } else {
              // If not in updateData, get ID from the original task if possible
              const originalAssignedTo = taskToUpdate.assigned_to;
              
              if (originalAssignedTo === null || originalAssignedTo === undefined) {
                cleanTask.assigned_to = null;
              } 
              // If it's already a number, use it
              else if (typeof originalAssignedTo === 'number') {
                cleanTask.assigned_to = originalAssignedTo;
              }
              // If it's a user object with an ID
              else if (typeof originalAssignedTo === 'object' && originalAssignedTo && originalAssignedTo.id) {
                cleanTask.assigned_to = parseInt(originalAssignedTo.id, 10);
              }
              // If it's a string that might be a user name, try to find the matching user
              else if (typeof originalAssignedTo === 'string') {
                // Look up user ID by name
                const matchingUser = users.find(u => u.name === originalAssignedTo);
                cleanTask.assigned_to = matchingUser ? matchingUser.id : null;
              }
              else {
                // Default to null if we can't determine anything useful
                cleanTask.assigned_to = null;
              }
            }
            
            console.log('Sending task update:', {
              id: taskId,
              cleanTask,
              assignedToType: typeof cleanTask.assigned_to,
              assignedToValue: cleanTask.assigned_to
            });
            
            const res = await axios.put('/api/tasks/' + taskId, cleanTask, config);

            // Update in local state - handling visibility based on tabs
            if (
              (activeTab === 'pending' && res.data.status === 'completed') ||
              (activeTab === 'completed' && res.data.status !== 'completed')
            ) {
              // Task should be removed from current tab
              updatedTasks = updatedTasks.filter(task => task.id !== taskId);
            } else {
              // Update task in the current view
              updatedTasks = updatedTasks.map(task => task.id === taskId ? res.data : task);
            }
          }
        }
      }

      // Update state with changes
      setTasks(updatedTasks);
      setSelectedTasks([]);
      setSelectAll(false);

      // Show success notification
      showNotification(`Successfully updated ${selectedTasks.length} tasks`, 'success');

    } catch (err) {
      console.error('Error executing bulk action:', err);

      // Show error notification
      showNotification('Failed to update tasks. Please try again.', 'error');
    }
  };

  const handleNewTaskChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for assigned_to to ensure it's the right type
    if (name === 'assigned_to' && value) {
      setNewTask({
        ...newTask,
        [name]: parseInt(value, 10) // Convert to integer for the API
      });
    } else {
      setNewTask({
        ...newTask,
        [name]: value
      });
    }
  };

  const handleSubmitNewTask = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Use the real API endpoint
      await axios.post(`/api/tasks`, newTask, config);

      // Instead of manually adding, just refetch the tasks list
      fetchTasks(); // This will fetch the current page with current filters/sort

      setShowNewTaskModal(false);

      // Reset the form with today's date and current user
      setNewTask(prevTask => ({
        title: '',
        description: '',
        due_date: getTodayDate(),
        due_time: '23:59', // Default to end of day
        priority: 'medium',
        assigned_to: prevTask.assigned_to, // Preserve the current user assignment
        lead_id: '',
        status: 'pending', // Default to pending for new tasks
        type: 'task'
      }));
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task. Please try again.');
    }
  };

  // State to track tasks being completed/reopened for visual feedback
  const [taskTransitions, setTaskTransitions] = useState({});
  
  // Handler for updating due date directly from the table
  const handleUpdateTaskDueDate = async (taskId, dueDate, dueTime) => {
    try {
      // Find the task to get its current details
      const taskToUpdate = tasks.find(task => task.id === taskId);
      if (!taskToUpdate) return;
      
      const token = localStorage.getItem('token');
      
      // Prepare the API request
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Create a clean task object with only necessary properties
      const payload = {
        title: taskToUpdate.title,
        description: taskToUpdate.description,
        priority: taskToUpdate.priority,
        status: taskToUpdate.status,
        type: taskToUpdate.type || 'task',
        // Include the due_date for backend validation
        due_date: dueDate
      };
      
      // Handle lead_id properly
      payload.lead_id = taskToUpdate.lead_id && typeof taskToUpdate.lead_id === 'object' 
        ? taskToUpdate.lead_id.id 
        : taskToUpdate.lead_id;
        
      // Handle assigned_to field
      if (taskToUpdate.assigned_to === null || taskToUpdate.assigned_to === undefined) {
        payload.assigned_to = null;
      } else if (typeof taskToUpdate.assigned_to === 'number') {
        payload.assigned_to = taskToUpdate.assigned_to;
      } else if (typeof taskToUpdate.assigned_to === 'object' && taskToUpdate.assigned_to.id) {
        payload.assigned_to = taskToUpdate.assigned_to.id;
      } else if (typeof taskToUpdate.assigned_to === 'string') {
        // Try to find user by name if it's a string
        const matchingUser = users.find(u => u.name === taskToUpdate.assigned_to);
        payload.assigned_to = matchingUser ? matchingUser.id : null;
      }
      
      // Combine date and time into ISO string for the API
      if (dueDate) {
        if (dueTime) {
          // Create a full ISO date time string
          const dateTimeString = `${dueDate}T${dueTime}:00`;
          payload.due_at = new Date(dateTimeString).toISOString();
        } else {
          // Default to end of day if no time specified
          const [year, month, day] = dueDate.split('-');
          const endOfDay = new Date(
            parseInt(year),
            parseInt(month) - 1, // Month is 0-indexed in JS
            parseInt(day),
            23, 59, 0
          );
          payload.due_at = endOfDay.toISOString();
        }
      }
      
      // Update the task via API
      const res = await axios.put(`/api/tasks/${taskId}`, payload, config);
      
      // Update task in the local state to avoid needing a full refetch
      setTasks(prevTasks => 
        prevTasks.map(task => task.id === taskId ? res.data : task)
      );
      
      // Show success notification
      showNotification('Due date updated successfully', 'success');
      
    } catch (err) {
      console.error('Error updating task due date:', err);
      showNotification('Failed to update due date', 'error');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    // Prevent duplicate actions if a transition is already happening for this task
    if (taskTransitions[taskId]) {
      return; // Task is already in transition, don't process another request
    }

    try {
      // Set visual transition state for this task using function form to ensure latest state
      setTaskTransitions(prev => ({
        ...prev,
        [taskId]: newStatus === 'completed' ? 'completing' : 'reopening'
      }));

      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Use the real API endpoint
      const res = await axios.patch(
        '/api/tasks/' + taskId + '/status',
        { status: newStatus },
        config
      );

      // Get task title before we potentially remove it from the list
      const taskTitle = tasks.find(t => t.id === taskId)?.title || 'Task';

      // Short delay for the animation/transition to be visible
      setTimeout(() => {
        if (activeTab === 'pending' && newStatus === 'completed') {
          // Remove the task from the list if it's now completed
          setTasks(prev => prev.filter(task => task.id !== taskId));
        } else if (activeTab === 'completed' && newStatus !== 'completed') {
          // Remove the task from the list if it's no longer completed
          setTasks(prev => prev.filter(task => task.id !== taskId));
        } else {
          // Update the task in the list using function form to ensure latest state
          setTasks(prev => prev.map(task =>
            task.id === taskId ? res.data : task
          ));
        }

        // Clear transition state using function form to ensure latest state
        setTaskTransitions(prev => {
          const newState = { ...prev };
          delete newState[taskId];
          return newState;
        });


        // For completed tasks, show notification with undo button
        if (newStatus === 'completed') {
          const taskData = res.data;
          // Show notification with undo action
          showNotification(`"${taskTitle}" marked as completed`, 'success', 8000, {
            text: 'Undo',
            onClick: async () => {
              try {
                const token = localStorage.getItem('token');
                const config = {
                  headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token
                  }
                };
                
                // Change status back to pending
                await axios.patch(
                  '/api/tasks/' + taskId + '/status',
                  { status: 'pending' },
                  config
                );
                
                // If we're in completed view, remove the task from the list
                if (activeTab === 'completed') {
                  setTasks(prev => prev.filter(task => task.id !== taskId));
                } else if (activeTab === 'pending') {
                  // Otherwise, add the updated task to the list
                  try {
                    const taskRes = await axios.get(`/api/tasks/${taskId}`, {
                      headers: { 'x-auth-token': token }
                    });
                    
                    if (taskRes.data) {
                      setTasks(prev => [taskRes.data, ...prev]);
                    }
                  } catch (fetchErr) {
                    console.error('Error fetching updated task:', fetchErr);
                  }
                }
                
                // Show notification for the undo action
                showNotification(`"${taskTitle}" has been reopened`, 'info');
              } catch (err) {
                console.error('Error undoing task completion:', err);
                showNotification('Failed to reopen task', 'error');
              }
            }
          });
        } else {
          showNotification(`"${taskTitle}" reopened`, 'info');
        }
      }, 500); // Adjusted delay
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status. Please try again.');

      // Clear transition state on error using function form to ensure latest state
      setTaskTransitions(prev => {
        const newState = { ...prev };
        delete newState[taskId];
        return newState;
      });
    }
  };

  // Task deletion states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Handle deleting a task
  const handleDeleteTask = async (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteConfirm(true);
  };

  // Execute task deletion
  const executeDeleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'x-auth-token': token
        }
      };

      await axios.delete('/api/tasks/' + taskId, config);

      // Remove the deleted task from the list
      setTasks(tasks.filter(task => task.id !== taskId));

      // Show success notification
      showNotification('Task successfully deleted', 'success');
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');

      // Show error notification
      showNotification(`Failed to delete task: ${err.response?.data?.message || err.message}`, 'error');
    }
  };

  // Handle editing a task
  const handleEditTask = (task) => {
    // Prepare task for editing with time component
    const taskWithTime = {...task};

    // Extract the time component from due_at for the form
    if (task.due_at) {
      const dueDateTime = new Date(task.due_at);

      // Format date as YYYY-MM-DD - HTML date input format
      const year = dueDateTime.getFullYear();
      const month = (dueDateTime.getMonth() + 1).toString().padStart(2, '0');
      const day = dueDateTime.getDate().toString().padStart(2, '0');
      taskWithTime.due_date = `${year}-${month}-${day}`;

      // Format time as HH:MM - HTML time input format
      const hours = dueDateTime.getHours().toString().padStart(2, '0');
      const minutes = dueDateTime.getMinutes().toString().padStart(2, '0');
      taskWithTime.due_time = `${hours}:${minutes}`;
    }

    // Handle assigned_to field which could be in various formats
    if (task.assigned_to) {
      // If it's an object with id property (from some API responses)
      if (typeof task.assigned_to === 'object' && task.assigned_to !== null && task.assigned_to.id) {
        taskWithTime.assigned_to = task.assigned_to.id;
      }
      // If it's a user ID as a string or number
      else if (typeof task.assigned_to === 'string' || typeof task.assigned_to === 'number') {
        // If it's a name string, try to find the matching user ID
        const matchingUser = users.find(u => u.name === task.assigned_to);
        if (matchingUser) {
          taskWithTime.assigned_to = matchingUser.id;
        } else {
          // Otherwise just use the value directly (might be an ID already)
          taskWithTime.assigned_to = task.assigned_to;
        }
      }
    } else {
      // Ensure unassigned tasks have an empty string for the select input
      taskWithTime.assigned_to = '';
    }

    setEditTask(taskWithTime);
    setShowEditTaskModal(true);
  };

  // handleEditTaskChange and handleSubmitEditTask are now inside EditTaskModal.js

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      // If already all selected, unselect all
      setSelectedTasks([]);
      setSelectAll(false);
    } else {
      // Select all tasks in current view
      setSelectedTasks(tasks.map(task => task.id));
      setSelectAll(true);
    }
  };

  return (
    <div className="w-full">
      {/* Using the shared TabNavigation component */}
      <TabNavigation
        tabs={[
          {
            id: 'pending',
            label: 'Pending',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            )
          },
          {
            id: 'completed',
            label: 'Completed',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )
          }
        ]}
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          localStorage.setItem('tasksActiveTab', tab);
        }}
      />


      {/* Filters and buttons section */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className={`px-4 py-2 flex items-center ${
              activeFilterCount > 0 ? '!bg-blue-100 !text-blue-800 !border-blue-300' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
            {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
          </Button>

          <Button
            onClick={() => setShowColumnModal(true)}
            variant="outline"
            className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1.5"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
            Columns
          </Button>
        </div>
        <Button
          onClick={() => setShowNewTaskModal(true)}
          variant="primary"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Task
        </Button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Filter Tasks</h3>
            <Button
              onClick={clearFilters}
              variant="link"
              className="text-sm"
            >
              Clear all filters
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                <option value="task">Sales Task</option>
                <option value="support">Support Ticket</option>
              </select>
            </div>

            {/* Assigned To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned To
              </label>
              <select
                name="assigned_to"
                value={filters.assigned_to}
                onChange={handleFilterChange}
                className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Assignees</option>
                <option value="unassigned">Unassigned</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Due Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <select
                name="due_date_after"
                value={filters.due_date_after}
                onChange={handleFilterChange}
                className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Due Dates</option>
                {getDateRangeOptions().map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              onClick={applyFilters}
              variant="primary"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="w-full bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            {/* Bulk Actions Bar - only shown when tasks are selected */}
            {selectedTasks.length > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 p-2 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''} selected
                  </span>
                  <Button
                    onClick={() => {
                      setSelectedTasks([]);
                      setSelectAll(false);
                    }}
                    variant="link"
                    className="text-xs"
                  >
                    Clear selection
                  </Button>
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => {
                      // If only one task is selected, open the single task edit modal
                      if (selectedTasks.length === 1) {
                        const taskToEdit = tasks.find(task => task.id === selectedTasks[0]);
                        if (taskToEdit) {
                          handleEditTask(taskToEdit);
                        }
                      } else {
                        // Open bulk update modal for multiple selections
                        setBulkAction('update');
                        setBulkActionData({
                          status: '',
                          due_date: '',
                          priority: '',
                          assigned_to: '',
                          type: ''
                        });
                        setShowBulkActionModal(true);
                      }
                    }}
                    variant="primary"
                    size="sm"
                  >
                    Update
                  </Button>
                  <Button
                    onClick={() => {
                      // Mark all selected tasks as completed
                      const completedIds = [...selectedTasks];
                      completedIds.forEach(taskId => {
                        handleStatusChange(taskId, 'completed');
                      });
                      setSelectedTasks([]);
                      setSelectAll(false);
                    }}
                    variant="success"
                    size="sm"
                  >
                    Mark Complete
                  </Button>
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="danger"
                    size="sm"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}

            <TasksTableWithShared
              tasks={tasks}
              selectedTasks={selectedTasks}
              setSelectedTasks={setSelectedTasks}
              selectAll={selectAll}
              setSelectAll={setSelectAll}
              handleSelectAll={handleSelectAll}
              handleEditTask={handleEditTask}
              handleDeleteTask={handleDeleteTask}
              handleStatusChange={handleStatusChange}
              taskTransitions={taskTransitions}
              users={users}
              pagination={pagination}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange} // Pass the handler
              sortBy={sortBy}
              sortOrder={sortOrder}
              handleSort={handleSort}
              showColumnModal={showColumnModal}
              setShowColumnModal={setShowColumnModal}
              loading={loading}
              handleUpdateTaskDueDate={handleUpdateTaskDueDate} // Pass the new handler
            />
          </div>
        )}
      </div>

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Create New Task</h3>

                    <form onSubmit={handleSubmitNewTask}>
                      <div className="mb-4">
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          id="title"
                          required
                          value={newTask.title}
                          onChange={handleNewTaskChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                        />
                      </div>

                      <div className="mb-4">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          name="description"
                          id="description"
                          rows="3"
                          value={newTask.description}
                          onChange={handleNewTaskChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                        ></textarea>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-1">
                            Due Date & Time
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="date"
                              name="due_date"
                              id="due_date"
                              required
                              value={newTask.due_date}
                              onChange={handleNewTaskChange}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                            />
                            <input
                              type="time"
                              name="due_time"
                              id="due_time"
                              value={newTask.due_time}
                              onChange={handleNewTaskChange}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                              placeholder="HH:MM"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                            Priority
                          </label>
                          <select
                            name="priority"
                            id="priority"
                            value={newTask.priority}
                            onChange={handleNewTaskChange}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700 mb-1">
                            Assign To
                          </label>
                          <select
                            name="assigned_to"
                            id="assigned_to"
                            value={newTask.assigned_to}
                            onChange={handleNewTaskChange}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                          >
                            <option value="">Select a user</option>
                            {users.map(user => (
                              <option key={user.id} value={user.id.toString()}>{user.name}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="lead_id" className="block text-sm font-medium text-gray-700 mb-1">
                            Related Lead
                          </label>
                          <LeadSearchInput
                            value={newTask.lead_id}
                            onChange={(leadId) => {
                              setNewTask({
                                ...newTask,
                                lead_id: leadId
                              });
                            }}
                            placeholder="Search leads by name or email"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <select
                          name="type"
                          id="type"
                          value={newTask.type}
                          onChange={handleNewTaskChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                        >
                          <option value="task">Sales Task</option>
                          <option value="support">Support Ticket</option>
                        </select>
                      </div>

                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                          Create Task
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowNewTaskModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render the imported Edit Task Modal */}
      <EditTaskModal
        isOpen={showEditTaskModal}
        onClose={(updated) => {
          setShowEditTaskModal(false);
          // Refetch tasks if the modal indicates an update occurred
          if (updated) {
            fetchTasks();
          }
        }}
        taskToEdit={editTask}
        users={users}
        // onTaskUpdated is handled by the onClose callback above
      />

      {/* Bulk Action Modal */}
      {showBulkActionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 md:mx-0 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Bulk Update Tasks</h3>
              <p className="text-sm text-gray-600">
                Apply updates to {selectedTasks.length} selected task(s):
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="space-y-4 pt-2">
                {/* Status */}
                <div>
                  <label htmlFor="bulkStatus" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    id="bulkStatus"
                    value={bulkActionData.status}
                    onChange={(e) => setBulkActionData({...bulkActionData, status: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No change</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* Due Date & Time */}
                <div>
                  <label htmlFor="bulkDueDate" className="block text-sm font-medium text-gray-700 mb-1">Due Date & Time</label>
                  <div className="flex space-x-2">
                    <input
                      type="date"
                      id="bulkDueDate"
                      value={bulkActionData.due_date}
                      onChange={(e) => setBulkActionData({...bulkActionData, due_date: e.target.value})}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    <input
                      type="time"
                      id="bulkDueTime"
                      value={bulkActionData.due_time}
                      onChange={(e) => setBulkActionData({...bulkActionData, due_time: e.target.value})}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="HH:MM"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Sets the same due date and time for all selected tasks. Leave empty for no change.
                  </p>
                </div>

                {/* Priority */}
                <div>
                  <label htmlFor="bulkPriority" className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    id="bulkPriority"
                    value={bulkActionData.priority}
                    onChange={(e) => setBulkActionData({...bulkActionData, priority: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No change</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                {/* Assigned To */}
                <div>
                  <label htmlFor="bulkAssignedTo" className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select
                    id="bulkAssignedTo"
                    value={bulkActionData.assigned_to}
                    onChange={(e) => setBulkActionData({...bulkActionData, assigned_to: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No change</option>
                    <option value="null">Unassign</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id.toString()}>{user.name}</option>
                    ))}
                  </select>
                </div>

                {/* Type */}
                <div>
                  <label htmlFor="bulkType" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    id="bulkType"
                    value={bulkActionData.type}
                    onChange={(e) => setBulkActionData({...bulkActionData, type: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No change</option>
                    <option value="task">Sales Task</option>
                    <option value="support">Support Ticket</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkActionModal(false);
                  setBulkAction('');
                  setBulkActionData({
                    status: '',
                    due_date: '',
                    due_time: '',
                    priority: '',
                    assigned_to: '',
                    type: ''
                  });
                }}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  executeBulkAction(bulkAction, bulkActionData);
                  setShowBulkActionModal(false);
                }}
                disabled={!bulkAction || (
                  bulkAction === 'update' &&
                  !bulkActionData.status &&
                  !bulkActionData.due_date &&
                  !bulkActionData.priority &&
                  !bulkActionData.assigned_to &&
                  !bulkActionData.type
                )}
                className="px-4 py-2 text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Update Tasks
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          if (taskToDelete) {
            // Single task deletion
            executeDeleteTask(taskToDelete);
            setTaskToDelete(null);
          } else if (selectedTasks.length > 0) {
            // Bulk task deletion
            const deleteIds = [...selectedTasks]; // Copy to prevent state issues during updates

            // Delete tasks one by one
            deleteIds.forEach(taskId => {
              executeDeleteTask(taskId);
            });

            setSelectedTasks([]);
            setSelectAll(false);
          }
          setShowDeleteConfirm(false);
        }}
        message={taskToDelete
          ? "Are you sure you want to delete this task? This action cannot be undone."
          : `Are you sure you want to delete ${selectedTasks.length} selected task${selectedTasks.length !== 1 ? 's' : ''}? This action cannot be undone.`
        }
      />
      
      {/* Lead Details Modal */}
      {showLeadDetailsModal && selectedLead && (
        <LeadDetailsModal
          isOpen={showLeadDetailsModal}
          onClose={() => setShowLeadDetailsModal(false)}
          lead={selectedLead}
          users={users}
          onLeadUpdate={(updatedLead) => {
            setSelectedLead(updatedLead);
            // No need to refresh tasks as lead changes don't affect task list
          }}
          onOpenEditModal={() => {
            // Close this modal and redirect to the lead edit view
            setShowLeadDetailsModal(false);
            window.location.href = `/leads/${selectedLead.id}`;
          }}
          onOpenEmailModal={() => {
            // Close this modal and redirect to the lead view
            setShowLeadDetailsModal(false);
            window.location.href = `/leads/${selectedLead.id}`;
          }}
        />
      )}
    </div>
  );
};

export default TasksList;
